// Auto-generated by scripts/split_calculators.py — keeps deterministic oilfield formulas in per-category modules.
import { round, num, positive, toRad } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive, toRad };

// ===== Category: directional =====
// --- Directional ---
// =========================================================================
// DIRECTIONAL DRILLING
// =========================================================================

// (toRad imported from _common.js)
export function doglegSeverity({ inc1, az1, inc2, az2, deltaMd }) {
  const I1 = num(inc1, "Inclination 1"); if (I1 < 0 || I1 > 180) throw new Error("Inclination 1 must be 0–180°");
  const A1 = num(az1, "Azimuth 1");
  const I2 = num(inc2, "Inclination 2"); if (I2 < 0 || I2 > 180) throw new Error("Inclination 2 must be 0–180°");
  const A2 = num(az2, "Azimuth 2");
  const dMD = positive(num(deltaMd, "ΔMD"), "ΔMD");
  // Dogleg angle (β) from minimum curvature
  const arg = Math.cos(toRad(I2 - I1)) - Math.sin(toRad(I1)) * Math.sin(toRad(I2)) * (1 - Math.cos(toRad(A2 - A1)));
  const clamped = Math.max(-1, Math.min(1, arg));
  const betaDeg = Math.acos(clamped) * 180 / Math.PI;
  const dls = (betaDeg * 100) / dMD;
  return {
    result: round(dls, 3),
    unit: "deg/100 ft",
    formula: "β = acos(cos(I2−I1) − sin(I1)·sin(I2)·(1−cos(A2−A1)));  DLS = β·100/ΔMD",
    steps: [
      `I1=${I1}°, A1=${A1}°, I2=${I2}°, A2=${A2}°, ΔMD=${dMD} ft`,
      `β = ${round(betaDeg, 4)}°`,
      `DLS = (${round(betaDeg, 4)} × 100) / ${dMD} = ${round(dls, 3)} °/100 ft`,
    ],
    inputs: { "Inc1 (°)": I1, "Az1 (°)": A1, "Inc2 (°)": I2, "Az2 (°)": A2, "ΔMD (ft)": dMD },
    extras: { "Dogleg Angle β (°)": round(betaDeg, 4) },
  };
}

export function mdToTvd({ inc1, inc2, deltaMd }) {
  const I1 = num(inc1, "Inclination 1"); if (I1 < 0 || I1 > 180) throw new Error("Inclination 1 must be 0–180°");
  const I2 = num(inc2, "Inclination 2"); if (I2 < 0 || I2 > 180) throw new Error("Inclination 2 must be 0–180°");
  const dMD = positive(num(deltaMd, "ΔMD"), "ΔMD");
  // Average angle method
  const avg = (I1 + I2) / 2;
  const dTVD = dMD * Math.cos(toRad(avg));
  return {
    result: round(dTVD, 2),
    unit: "ft (ΔTVD)",
    formula: "ΔTVD = ΔMD × cos((I1+I2)/2)   (average-angle method)",
    steps: [
      `Avg inclination = (${I1} + ${I2}) / 2 = ${round(avg, 2)}°`,
      `ΔTVD = ${dMD} × cos(${round(avg, 2)}°) = ${round(dTVD, 2)} ft`,
    ],
    inputs: { "Inc1 (°)": I1, "Inc2 (°)": I2, "ΔMD (ft)": dMD },
  };
}

export function buildRate({ inc1, inc2, deltaMd }) {
  const I1 = num(inc1, "Inclination 1");
  const I2 = num(inc2, "Inclination 2");
  const dMD = positive(num(deltaMd, "ΔMD"), "ΔMD");
  const br = ((I2 - I1) * 100) / dMD;
  return {
    result: round(br, 3),
    unit: "deg/100 ft",
    formula: "BUR = (I2 − I1) × 100 / ΔMD   (negative = drop)",
    steps: [
      `(${I2} − ${I1}) × 100 / ${dMD} = ${round(br, 3)} °/100 ft`,
      br >= 0 ? "Building inclination" : "Dropping inclination",
    ],
    inputs: { "Inc1 (°)": I1, "Inc2 (°)": I2, "ΔMD (ft)": dMD },
  };
}

export function horizontalDisplacement({ inc1, inc2, az1, az2, deltaMd }) {
  // Minimum curvature horizontal displacement (north / east increments)
  const I1 = num(inc1, "Inclination 1");
  const I2 = num(inc2, "Inclination 2");
  const A1 = num(az1, "Azimuth 1");
  const A2 = num(az2, "Azimuth 2");
  const dMD = positive(num(deltaMd, "ΔMD"), "ΔMD");
  const arg = Math.cos(toRad(I2 - I1)) - Math.sin(toRad(I1)) * Math.sin(toRad(I2)) * (1 - Math.cos(toRad(A2 - A1)));
  const beta = Math.acos(Math.max(-1, Math.min(1, arg)));
  const RF = beta === 0 ? 1 : Math.tan(beta / 2) * 2 / beta;
  const dN = (dMD / 2) * (Math.sin(toRad(I1)) * Math.cos(toRad(A1)) + Math.sin(toRad(I2)) * Math.cos(toRad(A2))) * RF;
  const dE = (dMD / 2) * (Math.sin(toRad(I1)) * Math.sin(toRad(A1)) + Math.sin(toRad(I2)) * Math.sin(toRad(A2))) * RF;
  const dH = Math.sqrt(dN * dN + dE * dE);
  return {
    result: round(dH, 2),
    unit: "ft (Δhorizontal)",
    formula: "Min-curvature: ΔN, ΔE → ΔH = √(ΔN² + ΔE²)",
    steps: [
      `β = ${round(beta * 180 / Math.PI, 4)}°, RF = ${round(RF, 5)}`,
      `ΔNorth = ${round(dN, 3)} ft`,
      `ΔEast  = ${round(dE, 3)} ft`,
      `ΔH = √(${round(dN, 3)}² + ${round(dE, 3)}²) = ${round(dH, 2)} ft`,
    ],
    inputs: { "Inc1 (°)": I1, "Az1 (°)": A1, "Inc2 (°)": I2, "Az2 (°)": A2, "ΔMD (ft)": dMD },
    extras: { "ΔNorth (ft)": round(dN, 3), "ΔEast (ft)": round(dE, 3) },
  };
}

// --- v1.4 — Directional additions ---
// ----- Directional additions -----

export function doglegSeverityBasic({ inc1, inc2, courseLength }) {
  const I1 = num(inc1, "Previous Inclination");
  const I2 = num(inc2, "Current Inclination");
  const L = positive(num(courseLength, "Course Length"), "Course Length");
  const dls = (Math.abs(I2 - I1) * 100) / L;
  return {
    result: round(dls, 3),
    unit: "deg/100 ft (basic)",
    formula: "DLS_basic ≈ |I2 − I1| × 100 / Course Length   (inclination-only; ignores azimuth)",
    steps: [
      `|${I2} − ${I1}| × 100 / ${L} = ${round(dls, 3)} °/100 ft`,
      "Note: simplified — for full curvature with azimuth use the DLS (min-curvature) calculator.",
    ],
    inputs: { "Inc1 (°)": I1, "Inc2 (°)": I2, "Course (ft)": L },
  };
}

export function turnRate({ az1, az2, courseLength }) {
  const A1 = num(az1, "Starting Azimuth");
  const A2 = num(az2, "Ending Azimuth");
  const L = positive(num(courseLength, "Course Length"), "Course Length");
  // shortest signed turn
  let d = A2 - A1;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  const tr = (d * 100) / L;
  return {
    result: round(tr, 3),
    unit: "deg/100 ft",
    formula: "Turn Rate = ΔAzimuth × 100 / Course Length   (signed, wrap-corrected)",
    steps: [
      `ΔAz (shortest) = ${round(d, 3)}°`,
      `Turn Rate = ${round(d, 3)} × 100 / ${L} = ${round(tr, 3)} °/100 ft`,
    ],
    inputs: { "Az1 (°)": A1, "Az2 (°)": A2, "Course (ft)": L },
  };
}


// =========================================================================
// ADVANCED DIRECTIONAL — multi-station survey + VS / closure
// =========================================================================

// Multi-station Minimum-Curvature survey. Input: CSV text "MD,INC,AZ" per line.
// Outputs cumulative TVD, ΔN, ΔE, closure distance + direction. Each station processed
// against the previous one. Empty/short input fails fast.
export function minCurvSurvey({ surveyCsv, targetAzimuth }) {
  if (!surveyCsv || typeof surveyCsv !== "string") throw new Error("Provide MD,INC,AZ CSV (one station per line)");
  const rows = surveyCsv
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith("#"))
    .map((l) => l.split(/[,\s]+/).map(Number));
  if (rows.length < 2) throw new Error("At least 2 stations are required (MD,INC,AZ)");
  for (const r of rows) {
    if (r.length < 3 || r.some((x) => !isFinite(x))) {
      throw new Error("Each station row must have 3 numbers: MD,INC,AZ");
    }
  }
  const tAz = num(targetAzimuth ?? 0, "Target Azimuth");

  let tvd = 0, ns = 0, ee = 0;
  const lines = [];
  for (let i = 1; i < rows.length; i++) {
    const [md1, i1, a1] = rows[i - 1];
    const [md2, i2, a2] = rows[i];
    const dMD = md2 - md1;
    if (dMD <= 0) throw new Error(`Row ${i + 1}: MD must increase`);
    const I1 = toRad(i1), I2 = toRad(i2), A1 = toRad(a1), A2 = toRad(a2);
    const cosBeta = Math.cos(I2 - I1) - Math.sin(I1) * Math.sin(I2) * (1 - Math.cos(A2 - A1));
    const beta = Math.acos(Math.max(-1, Math.min(1, cosBeta)));
    const RF = beta === 0 ? 1 : (2 / beta) * Math.tan(beta / 2);
    const dTVD = (dMD / 2) * (Math.cos(I1) + Math.cos(I2)) * RF;
    const dN = (dMD / 2) * (Math.sin(I1) * Math.cos(A1) + Math.sin(I2) * Math.cos(A2)) * RF;
    const dE = (dMD / 2) * (Math.sin(I1) * Math.sin(A1) + Math.sin(I2) * Math.sin(A2)) * RF;
    tvd += dTVD; ns += dN; ee += dE;
    const dls = (beta * 180 / Math.PI) * 100 / dMD;
    lines.push(`stn ${i}: MD ${md2}  TVD ${round(tvd, 1)}  N ${round(ns, 1)}  E ${round(ee, 1)}  DLS ${round(dls, 2)}°/100ft`);
  }
  const closure = Math.sqrt(ns * ns + ee * ee);
  const closureAz = ((Math.atan2(ee, ns) * 180 / Math.PI) + 360) % 360;
  const vs = ns * Math.cos(toRad(tAz)) + ee * Math.sin(toRad(tAz));
  return {
    result: round(tvd, 2),
    unit: "ft (final TVD)",
    formula: "Minimum-curvature multi-station: per-segment ΔTVD/ΔN/ΔE accumulated; VS = N·cos(tAz) + E·sin(tAz)",
    steps: [
      `${rows.length} stations processed`,
      ...lines,
      `Final TVD = ${round(tvd, 2)} ft`,
      `ΔN total = ${round(ns, 2)} ft,  ΔE total = ${round(ee, 2)} ft`,
      `Closure distance = ${round(closure, 2)} ft  @ ${round(closureAz, 2)}°`,
      `Vertical Section (target ${tAz}°) = ${round(vs, 2)} ft`,
    ],
    inputs: { "Stations": rows.length, "Target Az (°)": tAz },
    extras: {
      "Final TVD (ft)": round(tvd, 2),
      "ΔN (ft)": round(ns, 2),
      "ΔE (ft)": round(ee, 2),
      "Closure (ft)": round(closure, 2),
      "Closure Az (°)": round(closureAz, 2),
      "Vertical Section (ft)": round(vs, 2),
    },
  };
}

// Closure distance & direction from explicit N/E displacements.
export function closureFromNE({ northing, easting }) {
  const N = num(northing, "Northing");
  const E = num(easting, "Easting");
  const dist = Math.sqrt(N * N + E * E);
  const az = ((Math.atan2(E, N) * 180 / Math.PI) + 360) % 360;
  return {
    result: round(dist, 2),
    unit: "ft (closure)",
    formula: "Closure = √(N² + E²) ;  Az = atan2(E, N)",
    steps: [
      `N = ${N} ft,  E = ${E} ft`,
      `Closure = √(${N}² + ${E}²) = ${round(dist, 2)} ft`,
      `Closure Az = ${round(az, 2)}°`,
    ],
    inputs: { "Northing (ft)": N, "Easting (ft)": E },
    extras: { "Closure Az (°)": round(az, 2) },
  };
}

// =========================================================================
// STAGE 2 — single-station Departure & Vertical Section (simplified)
// These intentionally avoid tied-survey calculations and are clearly marked
// as single-station field estimates. Full survey work belongs in the
// Minimum-Curvature Survey calculator above or in approved directional software.
// =========================================================================

// Departure (horizontal projection) from a single station inclination & course length.
// Departure = Course Length × sin(inclination)
export function departureEstimate({ courseLength, inclination }) {
  const L = positive(num(courseLength, "Course Length"), "Course Length");
  const I = num(inclination, "Inclination");
  if (I < 0 || I > 180) throw new Error("Inclination must be 0–180°");
  const dep = L * Math.sin(toRad(I));
  return {
    result: round(dep, 2),
    unit: "ft",
    formula: "Departure = Course Length × sin(Inclination)   (simplified single-station)",
    steps: [
      `Course Length = ${L} ft,  Inc = ${I}°`,
      `Departure = ${L} × sin(${I}°)`,
      `Departure = ${round(dep, 2)} ft`,
    ],
    inputs: { "Course Length (ft)": L, "Inclination (°)": I },
    extras: { "Method": "Simplified single-station — assumes constant inclination over the segment" },
    worksheetWarning: true,
  };
}

// Vertical Section from a single station along a target azimuth.
// VS = Departure × cos(azimuth - target azimuth)
// Δazimuth is wrap-corrected to the shortest signed difference.
export function verticalSection({ departure, azimuth, targetAzimuth }) {
  const D = positive(num(departure, "Departure"), "Departure");
  const A = num(azimuth, "Azimuth");
  const tAz = num(targetAzimuth, "Target Azimuth");
  let dAz = A - tAz;
  while (dAz > 180) dAz -= 360;
  while (dAz < -180) dAz += 360;
  const vs = D * Math.cos(toRad(dAz));
  return {
    result: round(vs, 2),
    unit: "ft",
    formula: "VS = Departure × cos(Azimuth − Target Azimuth)   (simplified single-station)",
    steps: [
      `Departure = ${D} ft,  Azimuth = ${A}°,  Target Az = ${tAz}°`,
      `Δaz (shortest signed) = ${round(dAz, 2)}°`,
      `VS = ${D} × cos(${round(dAz, 2)}°) = ${round(vs, 2)} ft`,
    ],
    inputs: { "Departure (ft)": D, "Azimuth (°)": A, "Target Az (°)": tAz },
    extras: { "Δaz (shortest, °)": round(dAz, 2), "Method": "Simplified field estimate — full survey calculations should use approved directional software" },
    worksheetWarning: true,
  };
}

// Vertical Section from explicit displacements + target azimuth.
export function verticalSectionFromNE({ northing, easting, targetAzimuth }) {
  const N = num(northing, "Northing");
  const E = num(easting, "Easting");
  const tAz = num(targetAzimuth, "Target Azimuth");
  const vs = N * Math.cos(toRad(tAz)) + E * Math.sin(toRad(tAz));
  return {
    result: round(vs, 2),
    unit: "ft (vertical section)",
    formula: "VS = N · cos(Az_target) + E · sin(Az_target)",
    steps: [
      `N = ${N} ft,  E = ${E} ft,  Az_target = ${tAz}°`,
      `VS = ${N}·cos(${tAz}) + ${E}·sin(${tAz})`,
      `VS = ${round(vs, 2)} ft`,
    ],
    inputs: { "Northing (ft)": N, "Easting (ft)": E, "Target Az (°)": tAz },
  };
}
